import React from 'react';

interface LoadingSpinnerProps {
  message?: string;
  size?: number;
  className?: string;
  inline?: boolean;
  // Names the status for assistive technology. Needed whenever `message` is
  // empty, since the spinner is otherwise an unlabelled SVG.
  label?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = 'Loading...',
  size = 72,
  className = '',
  inline = false,
  label = 'Loading'
}) => {
  return (
    // <output> carries an implicit status role, so it announces the same way
    // without the explicit ARIA attribute.
    <output
      aria-label={message || label}
      className={`flex items-center justify-center ${
        inline ? 'flex-row gap-2' : 'flex-col py-8 gap-4'
      } ${className}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 72 72"
        fill="none"
        className="animate-spin"
        style={{ animationDuration: '2s' }}
      >
        <path
          d="M72 36C72 55.8823 55.8823 72 36 72C16.1177 72 0 55.8823 0 36C0 16.1177 16.1177 0 36 0C55.8823 0 72 16.1177 72 36ZM7.2 36C7.2 51.9058 20.0942 64.8 36 64.8C51.9058 64.8 64.8 51.9058 64.8 36C64.8 20.0942 51.9058 7.2 36 7.2C20.0942 7.2 7.2 20.0942 7.2 36Z"
          fill="#F3F4F6"
        />
        <g
          clipPath="url(#paint0_angular_6179_147332_clip_path)"
          data-figma-skip-parse="true"
        >
          <g transform="matrix(-0.0115 0.016 0.00642462 0.0046177 19 63.5)">
            <foreignObject
              x="-6810.57"
              y="-6810.57"
              width="13621.1"
              height="13621.1"
            >
              <div
                style={{
                  background:
                    'conic-gradient(from 90deg,rgba(11, 84, 37, 0.4809) 0deg,rgba(20, 128, 60, 1) 104.433deg,rgba(3, 45, 16, 0) 263.271deg,rgba(11, 84, 37, 0.4809) 360deg)',
                  height: '100%',
                  width: '100%',
                  opacity: 1
                }}
              />
            </foreignObject>
          </g>
        </g>
        <path d="M19.5095 63.8895C18.4975 65.6009 19.0587 67.8268 20.8626 68.6629C27.4948 71.7367 34.9356 72.7306 42.1925 71.4634C50.6365 69.9889 58.2772 65.55 63.7405 58.9449C69.2037 52.3398 72.1309 44.0021 71.9955 35.4314C71.8601 26.8608 68.6711 18.6197 63.0019 12.1904C57.3328 5.7612 49.5557 1.56575 41.0694 0.358712C32.5831 -0.848323 23.9446 1.01228 16.7076 5.60585C9.47068 10.1994 4.11027 17.2245 1.59055 25.4175C-0.574959 32.4587 -0.52018 39.9654 1.69935 46.9302C2.30304 48.8246 4.44122 49.6599 6.26585 48.8701C8.09049 48.0804 8.9056 45.9644 8.34946 44.0555C6.78325 38.6797 6.81118 32.9356 8.47243 27.534C10.4882 20.9796 14.7765 15.3595 20.5661 11.6847C26.3557 8.00982 33.2665 6.52134 40.0555 7.48697C46.8445 8.4526 53.0663 11.809 57.6016 16.9524C62.1369 22.0958 64.6881 28.6886 64.7964 35.5451C64.9047 42.4017 62.563 49.0718 58.1924 54.3559C53.8218 59.64 47.7092 63.1912 40.954 64.3707C35.3869 65.3428 29.6846 64.6516 24.547 62.4249C22.7228 61.6342 20.5214 62.178 19.5095 63.8895Z" />
        <defs>
          <clipPath id="paint0_angular_6179_147332_clip_path">
            <path d="M19.5095 63.8895C18.4975 65.6009 19.0587 67.8268 20.8626 68.6629C27.4948 71.7367 34.9356 72.7306 42.1925 71.4634C50.6365 69.9889 58.2772 65.55 63.7405 58.9449C69.2037 52.3398 72.1309 44.0021 71.9955 35.4314C71.8601 26.8608 68.6711 18.6197 63.0019 12.1904C57.3328 5.7612 49.5557 1.56575 41.0694 0.358712C32.5831 -0.848323 23.9446 1.01228 16.7076 5.60585C9.47068 10.1994 4.11027 17.2245 1.59055 25.4175C-0.574959 32.4587 -0.52018 39.9654 1.69935 46.9302C2.30304 48.8246 4.44122 49.6599 6.26585 48.8701C8.09049 48.0804 8.9056 45.9644 8.34946 44.0555C6.78325 38.6797 6.81118 32.9356 8.47243 27.534C10.4882 20.9796 14.7765 15.3595 20.5661 11.6847C26.3557 8.00982 33.2665 6.52134 40.0555 7.48697C46.8445 8.4526 53.0663 11.809 57.6016 16.9524C62.1369 22.0958 64.6881 28.6886 64.7964 35.5451C64.9047 42.4017 62.563 49.0718 58.1924 54.3559C53.8218 59.64 47.7092 63.1912 40.954 64.3707C35.3869 65.3428 29.6846 64.6516 24.547 62.4249C22.7228 61.6342 20.5214 62.178 19.5095 63.8895Z" />
          </clipPath>
        </defs>
      </svg>
      {message && (
        <span
          style={
            inline
              ? undefined
              : {
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '24px',
                  letterSpacing: '-0.12px',
                  color: 'var(--Text-Default-Text-Default-Primary, #14151C)'
                }
          }
        >
          {message}
        </span>
      )}
    </output>
  );
};

export default LoadingSpinner;
